import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext } from 'react';
import { httpWithAuth } from '~/clients';
import { VDP_PRICING } from '~/constants/endpoints';
import StaticContext from '~/context/common/StaticContext';
import TradeContext, {
  useGetAcquisitionNameForApiRequest,
  useIsTradeInApplied
} from '~/context/common/TradeContext';
import { useUserLocation } from '~/context/common/UserContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleId } from '~/context/vdp/VehicleContext';
import type { TradeInfo } from '~/contracts/srp';
import { VehiclePaymentTerm } from '~/contracts/vdp';
import FinanceInfoFromApi from '~/services/srp/contracts/FinanceInfo';
import { PricingError } from '~/services/srp/contracts/PricingResponse';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';
import useIsInSptOnVdpExperiment from '../useIsInSptOnVdpExperiment';

export type VdpPricingResponse = {
  financeInfo: FinanceInfoFromApi;
  tradeInfo?: TradeInfo;
  vehicleId: number;
  price: Partial<VehiclePaymentTerm>;
  errors: string[];
};

export default function useGetVDPPricing() {
  const { clientId } = useContext(StaticContext);
  const {
    vdpPricingInfo: { setData, setLoading, setError },
    fetchVdpPricingFlag,
    setPricingError
  } = useContext(UserVehicleContext);
  const {
    trades: { isLoaded: isTradesLoaded }
  } = useContext(TradeContext);
  const vehicleId = useGetVehicleId();
  const location = useUserLocation();
  const applyTradeIn = useIsTradeInApplied();
  const preferredAcquisitionName = useGetAcquisitionNameForApiRequest();
  const isInSptToVdpExperiment = useIsInSptOnVdpExperiment();

  useEffectWithController(
    controller => {
      if (!isInSptToVdpExperiment || !isTradesLoaded) return;
      setLoading();

      httpWithAuth
        .post<VdpPricingResponse>(
          VDP_PRICING,
          {
            applyVerboseLogging: false,
            applyTradeIn,
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            vehicleId: vehicleId,
            zip5: location.zip,
            preferredAcquisitionName,
            analyticsData: {
              //this is a required field for the endpoint we can send the rest of the data as empty. Ask the BE team if they need more data in this object.
              clientId
            }
          },
          {
            signal: controller.signal
          }
        )
        .then(({ data }) => {
          setData(data);
          if (data.errors.length > 0) {
            setPricingError(PricingError.NoVehiclePricingInfo);
          }
        })
        .catch(
          generateErrorLogger(
            'Get VDP Pricing',
            {},
            {
              callback: error => {
                setError(error);
                setPricingError(PricingError.ApiError);
              },
              controller
            }
          )
        );
    },
    [
      applyTradeIn,
      clientId,
      fetchVdpPricingFlag,
      isInSptToVdpExperiment,
      isTradesLoaded,
      location.zip,
      preferredAcquisitionName,
      setData,
      setError,
      setLoading,
      setPricingError,
      vehicleId
    ]
  );
}
